<template>
  <div class="admin-stores">
    <hs-table-shell
      title="Stores"
      description="Browse all stores on the platform. Super admins can transfer store ownership."
    >
      <template #filters>
        <div class="admin-stores__toolbar">
          <hs-input
            v-model="search"
            type="search"
            icon="search"
            placeholder="Search by store name or ID..."
          />
          <hs-button
            variant="secondary"
            icon="refresh-cw"
            :loading="loadingStores"
            @click="loadStores"
          >
            Refresh
          </hs-button>
        </div>
      </template>

      <div v-if="loadingStores" class="admin-stores__loading">
        <hs-skeleton variant="card" height="16rem" />
      </div>

      <hs-empty-state
        v-else-if="stores.length === 0"
        icon="shopping-bag"
        title="No stores found"
        description="Try a different search term."
      />

      <table
        v-else
        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
      >
        <thead class="bg-gray-50 dark:bg-gray-900">
          <tr>
            <th class="admin-stores__th">ID</th>
            <th class="admin-stores__th">Name</th>
            <th class="admin-stores__th">Owner ID</th>
            <th class="admin-stores__th">Type</th>
            <th class="admin-stores__th">Created</th>
            <th v-if="isSuperAdmin" class="admin-stores__th">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          <tr v-for="store in stores" :key="store.id">
            <td class="admin-stores__td">
              <span class="admin-stores__id">#{{ store.id }}</span>
            </td>
            <td class="admin-stores__td">
              <div class="admin-stores__name">{{ store.name }}</div>
              <div v-if="store.merchant_code" class="admin-stores__meta">
                {{ store.merchant_code }}
              </div>
            </td>
            <td class="admin-stores__td admin-stores__owner-id">
              {{ store.owner_id }}
            </td>
            <td class="admin-stores__td">
              <hs-badge tone="neutral" size="sm">
                {{ store.store_type || "RETAIL" }}
              </hs-badge>
            </td>
            <td class="admin-stores__td admin-stores__date">
              {{ formatDate(store.created_at) }}
            </td>
            <td v-if="isSuperAdmin" class="admin-stores__td">
              <hs-button
                variant="ghost"
                size="sm"
                icon="refresh-cw"
                @click="openTransferModal(store)"
              >
                Transfer ownership
              </hs-button>
            </td>
          </tr>
        </tbody>
      </table>

      <template #footer>
        <pagination-controls
          v-if="pagination.total > 0"
          :current-page="pagination.page"
          :total-pages="pagination.pages"
          :total="pagination.total"
          :per-page="pagination.perPage"
          :has-next="pagination.hasNext"
          :has-prev="pagination.hasPrev"
          items-label="stores"
          @page-change="handlePageChange"
          @per-page-change="handlePerPageChange"
        />
      </template>
    </hs-table-shell>

    <transfer-ownership-modal
      v-if="transferTarget"
      :store="transferTarget"
      @close="closeTransferModal"
      @transferred="handleTransferred"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import {
  HsBadge,
  HsButton,
  HsEmptyState,
  HsInput,
  HsSkeleton,
  HsTableShell,
} from "@/components/ui";
import PaginationControls from "@/components/common/PaginationControls.vue";
import TransferOwnershipModal from "@/components/modals/admin/TransferOwnershipModal.vue";

export default {
  name: "AdminStoresView",
  components: {
    HsBadge,
    HsButton,
    HsEmptyState,
    HsInput,
    HsSkeleton,
    HsTableShell,
    PaginationControls,
    TransferOwnershipModal,
  },
  setup() {
    const vuexStore = useStore();

    const search = ref("");
    const transferTarget = ref(null);

    const stores = computed(
      () => vuexStore.getters["adminStoreManagement/stores"]
    );
    const loadingStores = computed(
      () => vuexStore.getters["adminStoreManagement/loadingStores"]
    );
    const pagination = computed(
      () => vuexStore.getters["adminStoreManagement/storesPagination"]
    );
    const isSuperAdmin = computed(() => {
      const permissions =
        vuexStore.getters.getAuthenticatedUser?.admin_permissions;
      return Array.isArray(permissions) && permissions.includes("SUPER_ADMIN");
    });

    const loadStores = async () => {
      try {
        const params = {
          page: pagination.value.page,
          per_page: pagination.value.perPage,
        };
        if (search.value.trim()) {
          params.search = search.value.trim();
        }
        await vuexStore.dispatch("adminStoreManagement/fetchStores", params);
      } catch (err) {
        toast.error("Unable to load stores.");
      }
    };

    const handlePageChange = (page) => {
      vuexStore.commit("adminStoreManagement/SET_STORES_PAGINATION", {
        ...pagination.value,
        page,
      });
      loadStores();
    };

    const handlePerPageChange = (perPage) => {
      vuexStore.commit("adminStoreManagement/SET_STORES_PAGINATION", {
        ...pagination.value,
        perPage,
        page: 1,
      });
      loadStores();
    };

    const openTransferModal = (store) => {
      transferTarget.value = store;
    };

    const closeTransferModal = () => {
      transferTarget.value = null;
    };

    const handleTransferred = (updatedStore) => {
      toast.success(
        `Ownership of "${updatedStore?.name || "store"}" transferred successfully.`
      );
      transferTarget.value = null;
      loadStores();
    };

    const formatDate = (value) => {
      if (!value) return "—";
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    watch(search, () => {
      vuexStore.commit("adminStoreManagement/SET_STORES_PAGINATION", {
        ...pagination.value,
        page: 1,
      });
      loadStores();
    });

    onMounted(loadStores);

    return {
      search,
      stores,
      loadingStores,
      pagination,
      isSuperAdmin,
      transferTarget,
      loadStores,
      handlePageChange,
      handlePerPageChange,
      openTransferModal,
      closeTransferModal,
      handleTransferred,
      formatDate,
    };
  },
};
</script>

<style scoped>
.admin-stores {
  display: grid;
  gap: 1rem;
}

.admin-stores__toolbar {
  align-items: end;
  display: flex;
  gap: 0.65rem;
}

.admin-stores__toolbar :deep(.ui-input) {
  flex: 1;
}

.admin-stores__loading {
  padding: 1rem;
}

.admin-stores__th {
  padding: 0.75rem 1.5rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--hs-muted, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.admin-stores__td {
  padding: 1rem 1.5rem;
  white-space: nowrap;
  vertical-align: middle;
}

.admin-stores__id {
  color: var(--hs-muted, #64748b);
  font-size: 0.82rem;
  font-weight: 800;
}

.admin-stores__name {
  color: var(--hs-text, #0f172a);
  font-size: 0.9rem;
  font-weight: 900;
}

.admin-stores__meta,
.admin-stores__date,
.admin-stores__owner-id {
  color: var(--hs-muted, #64748b);
  font-size: 0.8rem;
  font-weight: 750;
}
</style>
